import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Upload, Download, Trash2 } from 'lucide-react';

// Gap Analysis — a titled repository of equipment service manuals (PDF).
// Every endpoint is behind requireGapAnalysisAccess on the server; App.js only
// shows this screen to the same allowlist (canUseGapAnalysis).
//
// Manuals are stored outside the web root, so the only way to get one is the
// authenticated download route — never a plain /uploads link.

const MAX_BYTES = 50 * 1024 * 1024;

const btn = (variant = 'secondary') => ({
  padding: '0.5rem 0.9rem',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  cursor: 'pointer',
  border: variant === 'secondary' ? '1px solid #d1d5db' : 'none',
  background: { primary: '#2563eb', danger: '#dc2626', secondary: 'white' }[variant],
  color: variant === 'secondary' ? '#374151' : 'white',
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap',
});
const smallBtn = { ...btn(), padding: '0.3rem 0.6rem', fontSize: '0.8rem' };
const disabledLook = { opacity: 0.5, cursor: 'not-allowed' };
const th = { textAlign: 'left', padding: '0.6rem 0.75rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '0.6rem 0.75rem', fontSize: '0.875rem', color: '#1f2937', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
const input = { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' };
const label = { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' };

const ErrorBox = ({ children }) => (
  <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
    {children}
  </div>
);

const readError = async (res) => {
  const j = await res.json().catch(() => ({}));
  return j.error || `Request failed (status ${res.status})`;
};

const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

export default function GapAnalysis() {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const [busyId, setBusyId] = useState(null);

  const fetchManuals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/gap-analysis/manuals');
      if (!res.ok) throw new Error(await readError(res));
      const json = await res.json();
      setManuals(Array.isArray(json.manuals) ? json.manuals : []);
    } catch (err) {
      setError(err.message || 'Failed to load manuals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchManuals(); }, [fetchManuals]);

  const pickFile = (f) => {
    setUploadError(null);
    if (!f) { setFile(null); return; }
    if (!/\.pdf$/i.test(f.name)) {
      setFile(null);
      setUploadError('Only PDF files can be uploaded.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (f.size > MAX_BYTES) {
      setFile(null);
      setUploadError('That file is larger than the 50 MB limit.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFile(f);
  };

  const trimmedTitle = title.trim();
  const canUpload = !!file && trimmedTitle.length > 0 && trimmedTitle.length <= 255 && !uploading;

  // XHR rather than fetch: fetch cannot report upload progress, and manuals
  // run up to 50 MB. XHR also skips index.js's fetch wrapper, so the token is
  // set here.
  const uploadManual = () => {
    if (!canUpload) return;
    setUploading(true);
    setUploadPct(0);
    setUploadError(null);

    const form = new FormData();
    form.append('title', trimmedTitle);
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/gap-analysis/manuals');
    const token = localStorage.getItem('pmplus_token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploadPct(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch (_) {
        // non-JSON body — fall through to the status-code message
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        setTitle('');
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchManuals();
      } else {
        setUploadError(data.error || `Upload failed (${xhr.status}).`);
      }
      setUploading(false);
    };

    xhr.onerror = () => {
      setUploadError('Connection failed. Please try again.');
      setUploading(false);
    };

    xhr.send(form);
  };

  // The download route needs the Authorization header, so a plain <a href>
  // would come back 401. Fetch it, then hand the blob to a throwaway anchor.
  const downloadManual = async (m) => {
    setBusyId(m.id);
    setError(null);
    let objectUrl = null;
    try {
      const res = await fetch(`/api/gap-analysis/manuals/${m.id}/download`);
      if (!res.ok) throw new Error(await readError(res));
      const blob = await res.blob();
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = m.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(`Could not download "${m.title}": ${err.message}`);
    }
    // Revoke on a delay — Safari can abort the save if the URL dies too soon.
    if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    setBusyId(null);
  };

  const deleteManual = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    setBusyId(m.id);
    setError(null);
    try {
      const res = await fetch(`/api/gap-analysis/manuals/${m.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readError(res));
      await fetchManuals();
    } catch (err) {
      setError(`Could not delete "${m.title}": ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="content-card" style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <h2 className="content-title" style={{ margin: 0 }}>Gap Analysis — Service Manuals</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            PDF service manuals, titled for lookup at the bench
          </p>
        </div>
        <button style={btn()} onClick={fetchManuals} disabled={loading}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', background: '#f9fafb' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 280px' }}>
            <label style={label} htmlFor="gap-title">Title</label>
            <input
              id="gap-title"
              style={input}
              type="text"
              maxLength={255}
              placeholder="e.g. Arjo Flowtron Universal DVT Service Manual"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
            />
          </div>
          <div style={{ flex: '1 1 220px' }}>
            <label style={label} htmlFor="gap-file">PDF file (max 50 MB)</label>
            <input
              id="gap-file"
              ref={fileInputRef}
              style={{ ...input, background: 'white' }}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => pickFile(e.target.files && e.target.files[0])}
              disabled={uploading}
            />
          </div>
          <button
            style={{ ...btn('primary'), ...(canUpload ? {} : disabledLook) }}
            onClick={uploadManual}
            disabled={!canUpload}
          >
            <Upload size={16} /> {uploading ? `Uploading… ${uploadPct}%` : 'Upload'}
          </button>
        </div>
        {uploading && (
          <div style={{ marginTop: '0.75rem', height: '0.375rem', background: '#e5e7eb', borderRadius: '999px', overflow: 'hidden' }}>
            <div style={{ width: `${uploadPct}%`, height: '100%', background: '#2563eb', transition: 'width 0.2s' }} />
          </div>
        )}
        {uploadError && <div style={{ marginTop: '0.75rem' }}><ErrorBox>{uploadError}</ErrorBox></div>}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {loading && manuals.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading manuals…</p>
      ) : manuals.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No manuals uploaded yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Title</th>
                <th style={th}>File</th>
                <th style={th}>Size</th>
                <th style={th}>Uploaded by</th>
                <th style={th}>Uploaded at</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {manuals.map((m) => (
                <tr key={m.id}>
                  <td style={{ ...td, fontWeight: '600' }}>{m.title}</td>
                  <td style={{ ...td, color: '#4b5563', wordBreak: 'break-all' }}>{m.original_filename}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatSize(m.size_bytes)}</td>
                  <td style={td}>{m.uploaded_by || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(m.uploaded_at)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                      <button style={smallBtn} onClick={() => downloadManual(m)} disabled={busyId === m.id}>
                        <Download size={14} /> Download
                      </button>
                      <button style={{ ...smallBtn, color: '#dc2626' }} onClick={() => deleteManual(m)} disabled={busyId === m.id}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
